import { isAxiosError } from 'axios';
import { ErrorReturnType, OrderItem } from '../../constants/types';
import { cartCheckedProps } from '../../store/features/cartSlice';
import { shutdownChannelTalk } from '../../components/ChannelTalk';
import { TodoMgmtSubTypeName } from '../../enum';
import html2canvas from 'html2canvas';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { CookieKey, createCookie } from '../../util/cookie';

dayjs.extend(isBetween);

type OriginalObject = {
  [key: string]: any;
};

type TransformedObject = {
  [key: string]: any;
};

export function transformKeys(obj: OriginalObject, value: string) {
  const result: TransformedObject = {};

  Object.keys(obj).forEach((key) => {
    const newKey = obj[key] || key;
    if (typeof newKey === 'string' && key === 'key') {
      // result[newKey] = obj[key];
      result[newKey] = value;
    }
  });

  return result;
}
export function groupBy<T>(
  iterable: Iterable<T>,
  fn: (item: T) => string | number,
) {
  return [...iterable].reduce<Record<string, T[]>>((groups, curr) => {
    const key = fn(curr);
    const group = groups[key] ?? [];
    group.push(curr);
    return { ...groups, [key]: group };
  }, {});
}

export function updateTarget(
  arr: cartCheckedProps[],
  id: string,
  updatedData: Partial<cartCheckedProps>,
): cartCheckedProps[] {
  const result = arr.map((item) =>
    item.id === id ? { ...item, ...updatedData } : item,
  );
  return result;
}

export function calcDateClone(
  date: Date,
  calc: { day?: number; month?: number; year?: number; sec?: number },
) {
  const result = new Date(date);
  calcDate(result, calc);
  return result;
}

export function calcDate(
  date: Date,
  calc: { day?: number; month?: number; year?: number; sec?: number },
) {
  if (calc.day) date.setDate(date.getDate() + calc.day);
  if (calc.month) date.setMonth(date.getMonth() + calc.month);
  if (calc.year) date.setFullYear(date.getFullYear() + calc.year);
  if (calc.sec) date.setSeconds(date.getSeconds() + calc.sec);
}

export function toDay(date: Date = new Date()) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
  );
}

export const handleCopyClipBoard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error(err);
    return false;
  }
};

export function dateFormattingToYYYYMMdd(date: Date) {
  const regStr = date
    .toLocaleDateString()
    .replace(/\s/gi, '')
    .replace(/\.$/g, '');
  return regStr.replaceAll('.', '-').toString();
}

export function changeTimestamp(date: any) {
  return date ? date.replace(/^(\d{4})-(\d{2})-(\d{2}).*$/, '$1.$2.$3') : '';
}

/** 종료일이 오늘인지 여부 (remain_days가 0이어도 종료일 당일은 이용 가능) */
export const isEndDateToday = (endDate?: string | null) =>
  endDate ? dayjs(endDate).isSame(dayjs(), 'day') : false;

export function calcRemainingPeriod(endDate: Date) {
  let remainingPeriod = Math.abs(endDate.getTime() - new Date().getTime());
  remainingPeriod = Math.ceil(remainingPeriod / (1000 * 60 * 60 * 24));

  return remainingPeriod;
}

export const getRandom = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min) + min);

export const calcPercent = (startDate: Date, endDate: Date) => {
  // 전체 기간 중 오늘부터 종료일까지 남은 기간
  const remainPeriod = endDate.getTime() - new Date().getTime();
  // 만약 종료일이 이미 지난 경우 0%를 반환합니다.
  if (remainPeriod <= 0) {
    return 0;
  }

  // 전체 기간 (종료일 - 오늘의 시작일)
  const totalPeriod = endDate.getTime() - startDate.getTime();

  const percent = Math.ceil((remainPeriod / totalPeriod) * 100);
  return percent;
};

export const totalDay = (startDate: Date, endDate: Date) => {
  const totalPeriod = Math.abs(startDate.getTime() - endDate.getTime());
  const totalDay = Math.ceil(totalPeriod / (1000 * 60 * 60 * 24));
  return totalDay;
};

export const remainDay = (endDate: Date) => {
  const remainPeriod = Math.abs(endDate.getTime() - new Date().getTime());
  const remainDay = Math.ceil(remainPeriod / (1000 * 60 * 60 * 24));
  return remainDay;
};

/**
 * YYYY년 MM월 DD일
 * @param date
 */
export const dateToKorean = (date: Date) => {
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};
/**
 * YYYY/MM/DD
 */
export const dateToSlash = (date: Date) => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  const formattedDate = new Intl.DateTimeFormat('ja-JP', options).format(date); // 'ja-JP' 형식을 사용하여 'year/month/day'로 지정
  return formattedDate.replace(/\s/g, ''); // 공백 제거
};
/**
 * YYYY-MM-DD
 */
export const dateToHyphen = (date: Date | string | null) => {
  try {
    if (!date) return '';

    const convertedDate = date instanceof Date ? date : new Date(date);

    // 유효하지 않은 날짜인 경우 빈 문자열 반환
    if (isNaN(convertedDate.getTime())) return '';

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
    const formattedDate = new Intl.DateTimeFormat('ko-KR', options).format(
      convertedDate,
    );
    return formattedDate.replace(/\s/g, '').replace(/\./g, '-').slice(0, -1);
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
};

export const openNewWindow = (url: string) => {
  window.open(url, '_blank');
};

export function deleteCookie(
  name: string,
  path: string = '/',
  domain?: string,
): void {
  let cookieStr = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;

  if (domain) {
    cookieStr += ` domain=${domain};`;
  }

  document.cookie = cookieStr;
}

export const logout = (gotoHome?: boolean | undefined) => {
  const loginInfo = [
    'persist:root',
    'userId',
    'accessToken',
    'type',
    'isActive',
  ];
  loginInfo.map((item) => localStorage.removeItem(item));
  if (gotoHome === undefined || gotoHome) {
    window.location.href = '/';
  }
  shutdownChannelTalk();

  createCookie(CookieKey.USER_INFO).removeCookie();
  try {
    createCookie(CookieKey.JEBS_AI_RECITATION).removeCookie();
  } catch (error) {
    // console.error(error);
  }
};

export function itemNameFormatter<
  T extends { [key: string]: any },
  K extends keyof T,
>(items: T[], key: K) {
  let rtn = '';
  if (!items) return rtn;

  if (items.length === 0) return rtn;

  rtn = items[0][key];

  if (items.length > 1) {
    rtn += ` 외 ${items.length - 1}건`;
  }

  return rtn;
}

export const getItemName = (orderItems: OrderItem[]) => {
  if (orderItems.length < 1) return '';

  if (orderItems.length === 1) return orderItems[0].product.name;

  return `${orderItems[0].product.name} 외 ${orderItems.length - 1}건`;
};

export function filterAndUpperCase(input: string): string {
  const filtered = input.replace(/[^a-zA-Z ]/g, '');
  const upperCased = filtered.toUpperCase();
  return upperCased;
}

export const getErrorMessages = (error: any) => {
  if (isAxiosError(error) && error.response) {
    try {
      const data = error.response?.data.error as ErrorReturnType;

      if (data) {
        const msg = data.message.reduce(
          (acc, cur) =>
            acc + `[${data.statusCode}] ` + cur.constraints.join(',') + '\n',
          '',
        );
        return msg;
      } else {
        if (typeof error.response?.data.message === 'string') {
          const msg = error.response?.data.message;
          return msg;
        } else {
          const msg = error.response?.data.message;
          return msg.constraints.join('\n');
        }
      }
    } catch (e) {
      return error.response.data.error;
    }
  } else {
    return '알 수 없는 오류가 발생했습니다.';
  }
};

/**
 * 만 14세 미만 여부.
 * 생년월일이 없거나 아직 덜 입력된 값('1990-01-')이면 판단하지 않고 false를 반환한다.
 */
export const check14UnderAge = (birth?: string | Date): boolean => {
  if (!birth) return false;

  const today = new Date();
  const birthDate = new Date(birth);
  if (isNaN(birthDate.getTime())) return false;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age < 14;
};

export const getSubTypeName = (subType: string) => {
  return TodoMgmtSubTypeName[subType as keyof typeof TodoMgmtSubTypeName];
};

export const downloadDiv = (id: string, filename: string) => {
  const style = document.createElement('style');
  document.head.appendChild(style);
  style.sheet?.insertRule(
    'body > div:last-child img { display: inline-block; }',
  );

  const imageDiv = document.getElementById(id);
  imageDiv!.style.display = 'block';

  const img = html2canvas(imageDiv, { scale: 10 }).then((canvas) => {
    style.remove();

    const imgData = canvas.toDataURL('image/png');
    // **다운 받지 않고 브라우저 확인용**
    // sample.current!.src = imgData;
    const link = document.createElement('a');
    link.href = imgData;
    link.download = `${filename}.png`;
    link.click();
  });
  return img;
};

/**
 * 기간 내에 있는지 확인
 * @param startDate 시작일 ('YYYY-MM-DD' | Dayjs)
 * @param endDate 종료일 ('YYYY-MM-DD' | Dayjs)
 * @param targetDate 타켓날짜 ('YYYY-MM-DD' | Dayjs) 입력 안 하면 오늘
 * @returns 기간 내에 있는지 여부
 */
export const isWithin = (
  startDate: string | Dayjs,
  endDate: string | Dayjs,
  targetDate?: string | Dayjs,
): boolean => {
  const start = typeof startDate === 'string' ? dayjs(startDate) : startDate;
  const end = typeof endDate === 'string' ? dayjs(endDate) : endDate;
  const target = dayjs(targetDate);
  return target.isBetween(start, end, 'day', '[]');
};

/**
 * 파일명 인코딩
 * @param url 파일 URL
 * @returns 인코딩된 파일 URL
 */
export const encodeFileNameInUrl = (url: string) => {
  const parts = url.split('/');
  const fileName = parts.pop(); // 마지막 요소가 파일명
  const encodedFileName = encodeURIComponent(fileName);
  parts.push(encodedFileName);
  return parts.join('/');
};

/**
 * UTC 기준 날짜 문자열을 KST 기준으로 변환
 * @param utcDateString UTC 기준 날짜 문자열
 * @param template 날짜 템플릿
 * @returns KST 기준 날짜 문자열
 */
export const formatToKstDate = (
  utcDateString: string,
  template: string = 'YYYY-MM-DD HH:mm:ss',
) => {
  if (!utcDateString) return '-';

  const targetKst = dayjs(utcDateString);

  return targetKst.format(template);
};

/**
 * 마감일 문자열을 오늘 기준으로 안내 문구로 변환
 * @param value 마감일 날짜 문자열
 * @returns 오늘이면 '오늘 마감', 내일이면 '내일까지', 그 외에는 'YY년 M월 D일' 형식의 문자열
 */
export const formatDueDateText = (value: string) => {
  const date = dayjs(value);

  if (!date.isValid()) return '-';

  const diffDate = date.startOf('day').diff(dayjs().startOf('day'), 'day');

  if (diffDate === 0) return '오늘 마감';
  if (diffDate === 1) return '내일까지';

  return date.format('YY년 M월 D일까지');
};
